import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
    Box,
    Paper,
    Typography,
    Grid,
    Card,
    CardContent,
    Divider,
    CircularProgress,
    useTheme,
    IconButton,
    Tooltip,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Tabs,
    Tab,
    TextField,
    InputAdornment
} from '@mui/material';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip as RechartsTooltip,
    ResponsiveContainer,
    PieChart,
    Pie,
    Cell,
    LineChart,
    Line,
    Legend
} from 'recharts';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import DevicesIcon from '@mui/icons-material/Devices';
import PeopleIcon from '@mui/icons-material/People';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import RedemptionTable from './RedemptionTable';
import api from '../../../services/api';
import SearchIcon from '@mui/icons-material/Search';
import DownloadIcon from '@mui/icons-material/Download';

interface TimeSeriesDataPoint {
    date: string;
    displayDate: string;
    scans: number;
    redemptions: number;
}

interface AnalyticsData {
    coupon: {
        _id: string;
        code: string;
        title: string;
        scanStats: {
            totalScans: number;
            uniqueScans: number;
            deviceBreakdown: {
                mobile: number;
                tablet: number;
                desktop: number;
            };
            formCompletions: number;
        };
        usageCount: number;
    };
    timeSeriesData: TimeSeriesDataPoint[];
}

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

function TabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`analytics-tabpanel-${index}`}
            aria-labelledby={`analytics-tab-${index}`}
            {...other}
        >
            {value === index && (
                <Box sx={{ py: 3 }}>
                    {children}
                </Box>
            )}
        </div>
    );
}

const formatDate = (dateStr: string): string => {
    // Append time in EST to ensure correct date interpretation
    const date = new Date(`${dateStr}T00:00:00-05:00`);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const processTimeSeriesData = (data: { date: string; scans: number; redemptions: number; }[]): TimeSeriesDataPoint[] => {
    console.log('Raw time series data:', data);
    
    // Ensure we have entries for all dates in the range
    const sortedData = data.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
    console.log('Sorted data:', sortedData);
    
    const result: TimeSeriesDataPoint[] = sortedData.map(item => ({
        ...item,
        displayDate: formatDate(item.date)
    }));
    
    console.log('Processed data:', result);
    return result;
};

const CouponAnalytics: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const [data, setData] = useState<AnalyticsData | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [timeRange, setTimeRange] = useState('7d');
    const [activeTab, setActiveTab] = useState(0);
    const [searchQuery, setSearchQuery] = useState('');
    const [searchTimeout, setSearchTimeout] = useState<NodeJS.Timeout | null>(null);
    const [downloading, setDownloading] = useState(false);
    const theme = useTheme();

    // Chart colors
    const colors = {
        primary: theme.palette.primary.main,
        secondary: theme.palette.secondary.main,
        success: theme.palette.success.main,
        error: theme.palette.error.main,
        mobile: '#FF6B6B',
        tablet: '#4ECDC4',
        desktop: '#45B7D1'
    };

    useEffect(() => {
        const fetchAnalytics = async () => {
            try {
                setLoading(true);
                const response = await api.get(`/admin/coupons/${id}/analytics`, {
                    params: { timeRange }
                });
                const rawData = response.data.data;
                console.log('API Response data:', rawData);
                
                setData({
                    ...rawData,
                    timeSeriesData: processTimeSeriesData(rawData.timeSeriesData)
                });
            } catch (err: any) {
                setError(err.response?.data?.message || 'Failed to load analytics');
            } finally {
                setLoading(false);
            }
        };

        if (id) {
            fetchAnalytics();
        }
    }, [id, timeRange]);

    const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        setSearchQuery(value);
        
        // Clear existing timeout
        if (searchTimeout) {
            clearTimeout(searchTimeout);
        }
        
        // Set new timeout to debounce API calls
        const timeout = setTimeout(() => {
            // We'll pass this value to RedemptionTable
            setSearchQuery(value);
        }, 500);
        
        setSearchTimeout(timeout);
    };

    const handleExport = async () => {
        try {
            setDownloading(true);
            const response = await api.get(`/admin/coupons/${id}/redemptions/export`, {
                responseType: 'blob'
            });
            
            // Create download link
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'redemptions.csv');
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.error('Export error:', err);
            // You might want to show an error message to the user here
        } finally {
            setDownloading(false);
        }
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
            </Box>
        );
    }

    if (error || !data) {
        return (
            <Box p={3}>
                <Typography color="error">{error || 'Failed to load analytics'}</Typography>
            </Box>
        );
    }

    const { coupon, timeSeriesData } = data;
    const conversionRate = coupon.scanStats.totalScans > 0
        ? ((coupon.usageCount / coupon.scanStats.totalScans) * 100).toFixed(1)
        : '0.0';

    const deviceData = [
        { name: 'Mobile', value: coupon.scanStats.deviceBreakdown.mobile },
        { name: 'Tablet', value: coupon.scanStats.deviceBreakdown.tablet },
        { name: 'Desktop', value: coupon.scanStats.deviceBreakdown.desktop }
    ];

    const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
        setActiveTab(newValue);
    };

    return (
        <Box p={3}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h4">
                    Analytics: {coupon.title}
                </Typography>
                <Box display="flex" alignItems="center" gap={2}>
                    {activeTab === 1 && (
                        <>
                            <Tooltip title="Export to CSV">
                                <IconButton 
                                    onClick={handleExport}
                                    disabled={downloading}
                                    size="small"
                                    sx={{ 
                                        bgcolor: 'background.paper',
                                        border: 1,
                                        borderColor: 'divider',
                                        '&:hover': {
                                            bgcolor: 'action.hover'
                                        }
                                    }}
                                >
                                    <DownloadIcon />
                                </IconButton>
                            </Tooltip>
                            <TextField
                                size="small"
                                variant="outlined"
                                placeholder="Search by email or last name..."
                                value={searchQuery}
                                onChange={handleSearch}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon />
                                        </InputAdornment>
                                    )
                                }}
                                sx={{ width: 300 }}
                            />
                        </>
                    )}
                    <FormControl variant="outlined" size="small" sx={{ minWidth: 120 }}>
                        <InputLabel>Time Range</InputLabel>
                        <Select
                            value={timeRange}
                            onChange={(e) => setTimeRange(e.target.value)}
                            label="Time Range"
                        >
                            <MenuItem value="7d">Last 7 days</MenuItem>
                            <MenuItem value="30d">Last 30 days</MenuItem>
                            <MenuItem value="90d">Last 90 days</MenuItem>
                            <MenuItem value="all">All time</MenuItem>
                        </Select>
                    </FormControl>
                </Box>
            </Box>

            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <Tabs value={activeTab} onChange={handleTabChange} aria-label="analytics tabs">
                    <Tab label="Overview" />
                    <Tab label="Redemptions" />
                </Tabs>
            </Box>

            <TabPanel value={activeTab} index={0}>
                {/* Overview Cards */}
                <Grid container spacing={3} mb={3}>
                    <Grid item xs={12} sm={6} md={2.4}>
                        <Card>
                            <CardContent>
                                <Box display="flex" alignItems="center" mb={1}>
                                    <QrCodeScannerIcon color="primary" />
                                    <Typography variant="h6" ml={1}>Total Scans</Typography>
                                </Box>
                                <Typography variant="h4">{coupon.scanStats.totalScans}</Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} sm={6} md={2.4}>
                        <Card>
                            <CardContent>
                                <Box display="flex" alignItems="center" mb={1}>
                                    <PeopleIcon color="secondary" />
                                    <Typography variant="h6" ml={1}>Unique Visitors</Typography>
                                </Box>
                                <Typography variant="h4">{coupon.scanStats.uniqueScans}</Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} sm={6} md={2.4}>
                        <Card>
                            <CardContent>
                                <Box display="flex" alignItems="center" mb={1}>
                                    <Typography variant="h6">Form Completions</Typography>
                                </Box>
                                <Typography variant="h4">{coupon.scanStats.formCompletions}</Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} sm={6} md={2.4}>
                        <Card>
                            <CardContent>
                                <Box display="flex" alignItems="center" mb={1}>
                                    <Typography variant="h6">Conversion Rate</Typography>
                                </Box>
                                <Typography variant="h4">
                                    {coupon.scanStats.totalScans > 0
                                        ? ((coupon.scanStats.formCompletions / coupon.scanStats.totalScans) * 100).toFixed(1)
                                        : '0.0'}%
                                </Typography>
                                <Typography variant="caption" color="textSecondary">
                                    Form Completion Rate
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} sm={6} md={2.4}>
                        <Card>
                            <CardContent>
                                <Box display="flex" alignItems="center" mb={1}>
                                    <Typography variant="h6">Redemption Rate</Typography>
                                </Box>
                                <Typography variant="h4">
                                    {coupon.scanStats.formCompletions > 0
                                        ? ((coupon.usageCount / coupon.scanStats.formCompletions) * 100).toFixed(1)
                                        : '0.0'}%
                                </Typography>
                                <Typography variant="caption" color="textSecondary">
                                    From Form Completions
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>

                {/* Charts */}
                <Grid container spacing={3}>
                    {/* Time Series Chart */}
                    <Grid item xs={12} lg={8}>
                        <Paper sx={{ p: 3 }}>
                            <Typography variant="h6" mb={2}>Scans & Redemptions Over Time</Typography>
                            <Box height={400}>
                                <ResponsiveContainer>
                                    <LineChart 
                                        data={timeSeriesData}
                                        margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                                    >
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis 
                                            dataKey="displayDate"
                                            interval={0}
                                            tickMargin={10}
                                        />
                                        <YAxis />
                                        <RechartsTooltip />
                                        <Legend />
                                        <Line
                                            type="monotone"
                                            dataKey="scans"
                                            stroke={colors.primary}
                                            strokeWidth={2}
                                            dot={true}
                                        />
                                        <Line
                                            type="monotone"
                                            dataKey="redemptions"
                                            stroke={colors.success}
                                            strokeWidth={2}
                                            dot={true}
                                        />
                                    </LineChart>
                                </ResponsiveContainer>
                            </Box>
                        </Paper>
                    </Grid>

                    {/* Device Breakdown */}
                    <Grid item xs={12} lg={4}>
                        <Paper sx={{ p: 3 }}>
                            <Typography variant="h6" mb={2}>Device Breakdown</Typography>
                            <Box height={400}>
                                <ResponsiveContainer>
                                    <PieChart>
                                        <Pie
                                            data={deviceData}
                                            dataKey="value"
                                            nameKey="name"
                                            cx="50%"
                                            cy="50%"
                                            innerRadius={60}
                                            outerRadius={100}
                                            label
                                        >
                                            <Cell fill={colors.mobile} />
                                            <Cell fill={colors.tablet} />
                                            <Cell fill={colors.desktop} />
                                        </Pie>
                                        <RechartsTooltip />
                                        <Legend />
                                    </PieChart>
                                </ResponsiveContainer>
                            </Box>
                        </Paper>
                    </Grid>
                </Grid>
            </TabPanel>

            <TabPanel value={activeTab} index={1}>
                <RedemptionTable couponId={id!} searchQuery={searchQuery} />
            </TabPanel>
        </Box>
    );
};

export default CouponAnalytics;
